import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Tooltip, ImageOverlay, Polyline, useMap } from "react-leaflet";
import axios from "axios";
import { API } from "../App";
import { createCustomIcon } from "../components/map/mapIcons";
import { MapController } from "../components/map/MapComponents";
import ZoneManager from "../components/ZoneManagerV2";
import L from "leaflet";
import { Activity, Users, Clock, Trash2, RefreshCw, TrendingUp, TrendingDown, X, Plus, Minus } from "lucide-react";
import { Layers, Eye, EyeOff } from "lucide-react";

const REFRESH_INTERVAL = 30000;
const DONUT_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7'];

function DonutChart({ data, title, size = 120 }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((a, d) => a + d.value, 0);
  if (total === 0) return null;
  const r = size / 2 - 4;
  const cx = size / 2, cy = size / 2;
  let cumAngle = -Math.PI / 2;
  const segments = data.map((d, i) => {
    const angle = (d.value / total) * Math.PI * 2;
    const x1 = cx + r * Math.cos(cumAngle);
    const y1 = cy + r * Math.sin(cumAngle);
    cumAngle += angle;
    const x2 = cx + r * Math.cos(cumAngle);
    const y2 = cy + r * Math.sin(cumAngle);
    const large = angle > Math.PI ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    return <path key={i} d={path} fill={DONUT_COLORS[i % DONUT_COLORS.length]} stroke="#09090b" strokeWidth="1" />;
  });
  return (
    <div className="text-center">
      <p className="text-[10px] text-zinc-500 font-mono uppercase mb-1">{title}</p>
      <div className="relative inline-block">
        <svg width={size} height={size}>{segments}</svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-zinc-950 rounded-full flex items-center justify-center" style={{ width: size * 0.45, height: size * 0.45 }}>
            <span className="text-xs font-bold text-white">{total}</span>
          </div>
        </div>
      </div>
      <div className="mt-1 space-y-0.5">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-1 text-[9px] font-mono">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="text-zinc-400 truncate">{d.label}</span>
            <span className="text-white font-bold ml-auto">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Zoom-knapper på kortet — store, finger-venlige til tablet-brug
function ZoomButtons() {
  const map = useMap();
  return (
    <div className="absolute bottom-4 right-4 z-[1000] flex flex-col gap-1.5" data-testid="live-monitor-zoom">
      <button
        onClick={() => map.setZoom(map.getZoom() + 0.25)}
        className="w-11 h-11 bg-white hover:bg-zinc-100 active:bg-zinc-200 rounded-lg shadow-lg border border-zinc-300 flex items-center justify-center text-zinc-900 transition-colors"
        title="Zoom ind (fint trin)"
        aria-label="Zoom ind"
        data-testid="live-monitor-zoom-in"
      >
        <Plus className="h-5 w-5" />
      </button>
      <button
        onClick={() => map.setZoom(map.getZoom() - 0.25)}
        className="w-11 h-11 bg-white hover:bg-zinc-100 active:bg-zinc-200 rounded-lg shadow-lg border border-zinc-300 flex items-center justify-center text-zinc-900 transition-colors"
        title="Zoom ud (fint trin)"
        aria-label="Zoom ud"
        data-testid="live-monitor-zoom-out"
      >
        <Minus className="h-5 w-5" />
      </button>
    </div>
  );
}

// Simpel ikon-faktor til GPS-trackere (mindre end ResourceMap's version, men genkendelig)
function trackerIcon(color = '#e11d48') {
  return L.divIcon({
    className: 'lm-tracker-icon',
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 0 2px ${color}aa, 0 2px 6px rgba(0,0,0,0.4);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

export default function LiveMonitor({ user, selectedEvent }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const eventId = selectedEvent?.id;
  const mapRef = useRef(null);

  const [points, setPoints] = useState([]);
  const [maxicontainers, setMaxicontainers] = useState([]);
  const [overlay, setOverlay] = useState(null);
  const [overlayBounds, setOverlayBounds] = useState([[55, 10], [56, 11]]);
  const [zones, setZones] = useState([]);
  const [stats, setStats] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [defaultLocation, setDefaultLocation] = useState(null);
  const [currentPhase, setCurrentPhase] = useState('operation');
  const [deadlines, setDeadlines] = useState([]);
  const [materielItems, setMaterielItems] = useState([]);
  const [deadlineTypes, setDeadlineTypes] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [nowTs, setNowTs] = useState(() => Date.now());

  // Layer toggles — gemmes lokalt så de huskes mellem sessioner.
  // Rengøringszoner OG service (drikkevand/pausetelt) er ALTID synlige.
  // Kun boder & barer (food/drinks), maxi, øer, spande, arbejdsområder, GPS kan slukkes.
  const [showIslands, setShowIslands] = useState(() => JSON.parse(localStorage.getItem('lm_showIslands') ?? 'true'));
  const [showBins, setShowBins] = useState(() => JSON.parse(localStorage.getItem('lm_showBins') ?? 'true'));
  const [showMaxis, setShowMaxis] = useState(() => JSON.parse(localStorage.getItem('lm_showMaxis') ?? 'true'));
  const [showWorkZones, setShowWorkZones] = useState(() => JSON.parse(localStorage.getItem('lm_showWorkZones') ?? 'true'));
  const [showBooths, setShowBooths] = useState(() => JSON.parse(localStorage.getItem('lm_showBooths') ?? 'true'));
  const [showGPS, setShowGPS] = useState(() => JSON.parse(localStorage.getItem('lm_showGPS') ?? 'true'));
  const [showLayerPanel, setShowLayerPanel] = useState(false);

  useEffect(() => { localStorage.setItem('lm_showIslands', JSON.stringify(showIslands)); }, [showIslands]);
  useEffect(() => { localStorage.setItem('lm_showBins', JSON.stringify(showBins)); }, [showBins]);
  useEffect(() => { localStorage.setItem('lm_showMaxis', JSON.stringify(showMaxis)); }, [showMaxis]);
  useEffect(() => { localStorage.setItem('lm_showWorkZones', JSON.stringify(showWorkZones)); }, [showWorkZones]);
  useEffect(() => { localStorage.setItem('lm_showBooths', JSON.stringify(showBooths)); }, [showBooths]);
  useEffect(() => { localStorage.setItem('lm_showGPS', JSON.stringify(showGPS)); }, [showGPS]);

  // "Nu" opdateres hvert 30. sek så elementer auto-falder af når deadline+30min er passeret
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const loadData = async () => {
    if (!eventId || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [pRes, mRes, oRes, zRes, sRes, eRes, dRes, matRes, dtRes, tRes] = await Promise.all([
        axios.get(`${API}/resource-points`, { headers }),
        axios.get(`${API}/maxicontainers?event_id=${eventId}`, { headers }),
        axios.get(`${API}/map-overlay?event_id=${eventId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/zones?event_id=${eventId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/events/${eventId}/analytics/extended`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/events/${eventId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/events/${eventId}/deadlines`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/events/${eventId}/materiel`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/deadline-types`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/events/${eventId}/gps-trackers`, { headers }).catch(() => ({ data: [] })),
      ]);
      setPoints(pRes.data.filter(p => p.event_id === eventId));
      setMaxicontainers(mRes.data || []);
      if (oRes.data) {
        setOverlay(oRes.data);
        if (oRes.data.bounds) setOverlayBounds(oRes.data.bounds);
      }
      setZones(zRes.data || []);
      if (sRes.data) setStats(sRes.data);
      if (eRes.data?.default_location) setDefaultLocation(eRes.data.default_location);
      if (eRes.data?.phase) setCurrentPhase(eRes.data.phase);
      setDeadlines(dRes.data || []);
      setMaterielItems(matRes.data || []);
      setDeadlineTypes(dtRes.data || []);
      setTrackers(tRes.data || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Monitor load error:", err);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [eventId]);

  // Beregn status-tæller baseret på aktiv fase
  const statusCounts = { green: 0, yellow: 0, red: 0 };
  points.forEach(p => {
    // I opbygning/nedpakning: brug setup_status. I afvikling: brug status
    const s = ['setup', 'teardown'].includes(currentPhase)
      ? (p.setup_status || 'red')
      : (p.status || 'grøn');
    if (s === 'red' || s === 'rød') statusCounts.red++;
    else if (s === 'yellow' || s === 'gul') statusCounts.yellow++;
    else statusCounts.green++;
  });

  const totalEmptyings = stats?.total_emptyings || 0;

  // Per-type fremdrift i opbygning/nedtagning (ressourceøer/ressourcespande/maxicontainere)
  const inSetupTeardown = ['setup', 'teardown'].includes(currentPhase);
  const calcTypeProgress = (items) => {
    let green = 0, yellow = 0, red = 0;
    items.forEach(it => {
      const s = it.setup_status || 'red';
      if (s === 'green') green++; else if (s === 'yellow') yellow++; else red++;
    });
    const total = items.length;
    return { total, green, yellow, red, pct: total > 0 ? Math.round(green / total * 100) : 0 };
  };
  const typeProgress = inSetupTeardown ? {
    ressourceoer: calcTypeProgress(points.filter(p => p.point_type === 'ressourceø')),
    ressourcespande: calcTypeProgress(points.filter(p => p.point_type === 'ressourcespand')),
    maxicontainere: calcTypeProgress(maxicontainers),
  } : null;
  const avgResponseMin = stats?.avg_response_time_minutes ? Math.round(stats.avg_response_time_minutes) : '-';

  // Top 5 travleste punkter per type
  const pointTypeMap = {};
  points.forEach(p => { pointTypeMap[p.name] = p.point_type; });
  const emptyingsByPoint = stats?.emptyings?.by_point || {};
  const getTop5 = (type) => Object.entries(emptyingsByPoint)
    .filter(([name]) => pointTypeMap[name] === type)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([label, value]) => ({ label, value }));
  const top5Islands = getTop5('ressourceø');
  const top5Bins = getTop5('ressourcespand');
  const top5Maxi = maxicontainers.length > 0
    ? Object.entries(emptyingsByPoint)
        .filter(([name]) => maxicontainers.some(m => m.name === name))
        .sort((a, b) => b[1] - a[1]).slice(0, 5)
        .map(([label, value]) => ({ label, value }))
    : [];

  // Sorteringskvalitet
  const sortQ = stats?.sorting_quality?.overall || {};
  const totalSort = (sortQ.good || 0) + (sortQ.medium || 0) + (sortQ.poor || 0);
  const sortPct = totalSort > 0 ? Math.round(((sortQ.good || 0) / totalSort) * 100) : null;

  // Sortering per punkt — top 5 bedste og dårligste
  const sortByPoint = stats?.sorting_quality?.by_point || {};
  const pointSortScores = Object.entries(sortByPoint).map(([name, sq]) => {
    const tot = (sq.good || 0) + (sq.medium || 0) + (sq.poor || 0);
    return { label: name, pct: tot > 0 ? Math.round(((sq.good || 0) / tot) * 100) : 0, total: tot };
  }).filter(p => p.total >= 1);
  const best5 = [...pointSortScores].sort((a, b) => b.pct - a.pct).slice(0, 5);
  const worst5 = [...pointSortScores].sort((a, b) => a.pct - b.pct).slice(0, 5);

  // === DAGENS PROGRAM ===
  // Sammenflet custom deadlines + materiel-deadlines for I DAG.
  // Hvert punkt vises til (deadline + 30 min). Punkter uden klokkeslæt vises hele dagen.
  const todaysAgenda = (() => {
    const now = new Date(nowTs);
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;  // lokal dato

    // Lookup type → farve
    const colorByType = {};
    deadlineTypes.forEach(t => { if (t.id && t.color) colorByType[t.id] = t.color; });

    const items = [];

    // Custom deadlines
    deadlines.forEach(d => {
      const dateStr = (d.date || '').slice(0, 10);
      if (dateStr !== todayStr) return;
      const color = d.custom_color || colorByType[d.type] || '#10b981';
      const label = d.custom_name || (deadlineTypes.find(t => t.id === d.type)?.name) || 'Andet';
      items.push({
        id: d.id,
        title: d.title || 'Uden titel',
        time: d.time || null,
        color,
        category: label,
      });
    });

    // Materiel: bestillings- og endelige deadlines
    materielItems.forEach(m => {
      if (m.deadline_bestilling && m.deadline_bestilling.slice(0, 10) === todayStr) {
        items.push({
          id: `mat-best-${m.id}`,
          title: `Bestilling: ${m.produktinformation || m.omraade || 'Materiel'}`,
          time: null,
          color: '#3b82f6',
          category: 'Indkøb',
        });
      }
      if (m.deadline_endelig && m.deadline_endelig.slice(0, 10) === todayStr) {
        items.push({
          id: `mat-end-${m.id}`,
          title: `Endelig: ${m.produktinformation || m.omraade || 'Materiel'}`,
          time: null,
          color: '#f97316',
          category: 'Levering',
        });
      }
    });

    // Filter: vis kun til (deadline + 30 min). Items uden tid: hele dagen.
    const visible = items.filter(it => {
      if (!it.time) return true;  // vises indtil midnat (todayStr-filter sørger for det)
      const [h, m] = it.time.split(':').map(Number);
      const deadline = new Date(yyyy, now.getMonth(), now.getDate(), h, m, 0).getTime();
      return now.getTime() <= deadline + 30 * 60 * 1000;
    });

    // Sort: items med tid først (kronologisk), derefter items uden tid
    visible.sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return 1;
      if (!b.time) return -1;
      return a.time.localeCompare(b.time);
    });

    return visible;
  })();

  const mapCenter = defaultLocation
    ? [defaultLocation.lat, defaultLocation.lng]
    : [(overlayBounds[0][0] + overlayBounds[1][0]) / 2, (overlayBounds[0][1] + overlayBounds[1][1]) / 2];

  return (
    <div className="h-screen w-screen flex bg-white overflow-hidden" data-testid="live-monitor">
      {/* KORT — 75% */}
      <div className="flex-[3] relative">
        <MapContainer
          center={mapCenter}
          zoom={15}
          className="h-full w-full"
          zoomControl={false}
          attributionControl={false}
          zoomSnap={0.25}
          zoomDelta={0.25}
          wheelPxPerZoomLevel={120}
          ref={mapRef}
        >
          <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
          <MapController center={mapCenter} zoom={15} overlayBounds={overlay ? overlayBounds : null} />

          {overlay && (
            <ImageOverlay
              url={`${API}/map-overlay/${overlay.id}/image`}
              bounds={overlayBounds}
              opacity={overlay.opacity || 0.7}
            />
          )}

          {/* Zoner (legacy) */}
          {zones.filter(z => z.type === 'area' && z.coordinates?.length > 2).map(zone => (
            <Polyline key={zone.id} positions={[...zone.coordinates, zone.coordinates[0]]}
              pathOptions={{ color: '#22c55e', weight: 2, fillOpacity: 0.05 }} />
          ))}

          {/* Zone Manager — viser rengøringszoner (ALTID), evt. arbejdszoner.
              Områder skjules her, da Live Monitor skal være visuelt enkelt. */}
          <ZoneManager
            eventId={eventId}
            userRole={user?.role}
            userSelectedRole={user?.selected_role}
            showAreas={false}
            showWorkZones={showWorkZones}
            showCleaningZones={true}
            drawCommand={null}
            onDrawComplete={() => {}}
            onZoneSelect={() => {}}
          />

          {/* Ressourcepunkter — filtreret efter lag-toggles.
              Service-punkter (drikkevand, pausetelt) er ALTID synlige som rengøringszoner.
              Kun boder & barer (food/drinks) kan slukkes via showBooths. */}
          {points.filter(p => {
            if (!p.lat || !p.lng) return false;
            if (p.point_type === 'ressourceø' && !showIslands) return false;
            if (p.point_type === 'ressourcespand' && !showBins) return false;
            // Boder & barer = food/drinks (kan slukkes)
            if (['food', 'drinks'].includes(p.point_type) && !showBooths) return false;
            // drikkevand + pausetelt = service = altid synlige
            return true;
          }).map(point => {
            const inSetupTeardown = ['setup', 'teardown'].includes(currentPhase);
            const isOperationalPoint = !!point.operational && inSetupTeardown;
            // Operationelle øer: brug drift-status (turkis/lilla), ikke opbygnings-status
            const effectiveStatus = isOperationalPoint
              ? point.status
              : (inSetupTeardown
                  ? (point.setup_status === 'green' ? 'grøn' : point.setup_status === 'yellow' ? 'gul' : point.setup_status === 'red' ? 'rød' : point.status)
                  : point.status);
            return (
            <Marker
              key={point.id}
              position={[point.lat, point.lng]}
              icon={createCustomIcon(point.point_type, effectiveStatus, point.bin_fraction, 
                (!isOperationalPoint && inSetupTeardown) ? point.setup_status : null, 
                point.bins, 16, point.containers, null, isOperationalPoint)}
            >
              <Tooltip direction="top" offset={[0, -15]} className="monitor-tooltip">
                <div className="text-xs font-mono">
                  <strong>{point.name}</strong>
                  {point.containers?.map((c, i) => (
                    <div key={i}>{c.count}x {c.container_size} {c.fraction_type}</div>
                  ))}
                  {point.bins?.map((b, i) => (
                    <div key={i}>{b.count}x {b.fraction_type}</div>
                  ))}
                </div>
              </Tooltip>
            </Marker>
            );
          })}

          {/* Maxicontainere */}
          {showMaxis && maxicontainers.filter(m => m.lat && m.lng).map(m => (
            <Marker
              key={m.id}
              position={[m.lat, m.lng]}
              icon={createCustomIcon('maxicontainer', m.status)}
            >
              <Tooltip direction="top" offset={[0, -15]}>
                <div className="text-xs font-mono"><strong>{m.name}</strong><br/>{m.container_type} — {m.fraction}</div>
              </Tooltip>
            </Marker>
          ))}

          {/* GPS Trackere — vises kun hvis admin har slået dem til + position er sat */}
          {showGPS && trackers.filter(t => t.lat && t.lng && t.lat !== 0).map(tracker => (
            <Marker
              key={`gps-${tracker.id}`}
              position={[tracker.lat, tracker.lng]}
              icon={trackerIcon(tracker.color || '#e11d48')}
            >
              <Tooltip direction="top" offset={[0, -10]}>
                <span className="text-xs font-bold">{tracker.name}</span>
                {(tracker.speed || 0) > 0.5 && (
                  <span className="text-xs ml-1">({((tracker.speed || 0) * 3.6).toFixed(0)} km/t)</span>
                )}
              </Tooltip>
            </Marker>
          ))}

          {/* Zoom-knapper (tablet-venlige) */}
          <ZoomButtons />
        </MapContainer>

        {/* Event-navn overlay */}
        <div className="absolute top-4 left-4 z-[1000] bg-white/85 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md border border-zinc-200">
          <h1 className="text-2xl font-black uppercase tracking-tight text-zinc-900">
            {selectedEvent?.name || 'Live Monitor'}
          </h1>
          <p className="text-xs text-zinc-500 font-mono">LIVE MONITOR</p>
        </div>

        {/* Luk-knap øverst til højre på kortet — kommer ud af live monitor uden at lukke appen */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 z-[1000] bg-white/85 backdrop-blur-sm hover:bg-white rounded-lg p-2.5 shadow-md border border-zinc-200 transition-colors"
          title="Luk Live Monitor"
          data-testid="live-monitor-close-btn"
          aria-label="Luk Live Monitor"
        >
          <X className="h-5 w-5 text-zinc-900" />
        </button>

        {/* Lag-knap (lige under luk-knappen) — toggler en lille panel med synlighedsvalg */}
        <button
          onClick={() => setShowLayerPanel(v => !v)}
          className={`absolute top-16 right-4 z-[1000] backdrop-blur-sm rounded-lg p-2.5 shadow-md border border-zinc-200 transition-colors ${showLayerPanel ? 'bg-zinc-900 text-white' : 'bg-white/85 hover:bg-white text-zinc-900'}`}
          title="Lag-valg"
          data-testid="live-monitor-layers-btn"
          aria-label="Vælg synlige lag"
        >
          <Layers className="h-5 w-5" />
        </button>

        {showLayerPanel && (
          <div className="absolute top-16 right-16 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-zinc-200 p-3 w-56" data-testid="live-monitor-layer-panel">
            <h3 className="text-xs uppercase font-bold text-zinc-500 tracking-wider mb-2">Synlige lag</h3>
            <div className="space-y-1">
              {[
                ['Ressourceøer', showIslands, setShowIslands, 'layer-islands'],
                ['Skraldespande', showBins, setShowBins, 'layer-bins'],
                ['Maxicontainere', showMaxis, setShowMaxis, 'layer-maxis'],
                ['Boder & barer', showBooths, setShowBooths, 'layer-booths'],
                ['Arbejdsområder', showWorkZones, setShowWorkZones, 'layer-workzones'],
                ['GPS-trackere', showGPS, setShowGPS, 'layer-gps'],
              ].map(([label, value, setter, tid]) => (
                <button
                  key={tid}
                  onClick={() => setter(!value)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${value ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'}`}
                  data-testid={tid}
                >
                  <span>{label}</span>
                  {value ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 opacity-50" />}
                </button>
              ))}
              <div className="text-[10px] text-zinc-500 italic px-2 pt-1 border-t border-zinc-200 mt-2 leading-snug">
                Rengøringszoner og service (drikkevand, pausetelt) vises altid
              </div>
            </div>
          </div>
        )}
      </div>

      {/* STATISTIK PANEL — smal, sort, fuld højde */}
      <div className="bg-zinc-950 border-l border-zinc-800 p-3 flex flex-col gap-3 w-[230px] min-w-[230px] max-w-[230px] overflow-y-auto">
        <div>
          <h2 className="text-base font-bold text-white uppercase tracking-tight mb-1">Status</h2>
          <p className="text-[10px] text-zinc-500 font-mono flex items-center gap-1.5 leading-tight">
            <RefreshCw className="h-3 w-3 animate-spin flex-shrink-0" style={{ animationDuration: '3s' }} />
            <span>{currentPhase === 'setup' ? 'Opbygning' : currentPhase === 'operation' ? 'Afvikling' : currentPhase === 'teardown' ? 'Nedpakning' : 'Planlægning'} · 30 sek</span>
          </p>
        </div>

        {/* Status-kort — stacket vertikalt, større tal */}
        <div className="flex flex-col gap-2">
          <div className="bg-green-500/15 border border-green-500/40 rounded-lg p-3 flex items-center justify-between">
            <div className="text-[11px] text-green-300 font-mono uppercase tracking-wider">Grøn</div>
            <div className="text-4xl font-black text-green-400 leading-none">{statusCounts.green}</div>
          </div>
          <div className="bg-yellow-500/15 border border-yellow-500/40 rounded-lg p-3 flex items-center justify-between">
            <div className="text-[11px] text-yellow-300 font-mono uppercase tracking-wider">Gul</div>
            <div className="text-4xl font-black text-yellow-400 leading-none">{statusCounts.yellow}</div>
          </div>
          <div className="bg-red-500/15 border border-red-500/40 rounded-lg p-3 flex items-center justify-between">
            <div className="text-[11px] text-red-300 font-mono uppercase tracking-wider">Rød</div>
            <div className="text-4xl font-black text-red-400 leading-none">{statusCounts.red}</div>
          </div>
        </div>

        {/* Split pr. type i opbygning/nedtagning */}
        {typeProgress && (
          <div className="flex flex-col gap-2" data-testid="lm-phase-by-type">
            {[
              { key: 'ressourceoer', label: 'Ressourceøer' },
              { key: 'ressourcespande', label: 'Ressourcespande' },
              { key: 'maxicontainere', label: 'Maxicontainere' },
            ].filter(t => typeProgress[t.key].total > 0).map(t => {
              const d = typeProgress[t.key];
              return (
                <div key={t.key} className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-2.5" data-testid={`lm-type-${t.key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-300">{t.label}</span>
                    <span className="text-xs font-mono text-zinc-400">{d.green}/{d.total} ({d.pct}%)</span>
                  </div>
                  <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${d.pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Nøgletal */}
        <div className="grid grid-cols-3 gap-1.5">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-center">
            <Trash2 className="h-4 w-4 text-primary mx-auto mb-0.5" />
            <div className="text-base font-black text-white leading-none">{totalEmptyings}</div>
            <div className="text-[8px] text-zinc-500 font-mono mt-1">Tømninger</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-center">
            <Clock className="h-4 w-4 text-blue-400 mx-auto mb-0.5" />
            <div className="text-base font-black text-white leading-none">{avgResponseMin}<span className="text-[9px]">m</span></div>
            <div className="text-[8px] text-zinc-500 font-mono mt-1">Respons</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-center">
            <Activity className="h-4 w-4 text-purple-400 mx-auto mb-0.5" />
            <div className="text-base font-black text-white leading-none">{points.length + maxicontainers.length}</div>
            <div className="text-[8px] text-zinc-500 font-mono mt-1">Punkter</div>
          </div>
        </div>

        {/* Sorteringskvalitet */}
        {sortPct !== null && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
            <p className="text-[10px] text-zinc-500 font-mono uppercase mb-2">Sorteringskvalitet</p>
            <div className="flex items-center gap-2">
              <div className={`text-2xl font-black ${sortPct >= 70 ? 'text-green-400' : sortPct >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                {sortPct}%
              </div>
              <div className="flex-1 h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: `${(sortQ.good || 0) / totalSort * 100}%`, display: 'inline-block' }} />
                <div className="h-full bg-yellow-500" style={{ width: `${(sortQ.medium || 0) / totalSort * 100}%`, display: 'inline-block' }} />
                <div className="h-full bg-red-500" style={{ width: `${(sortQ.poor || 0) / totalSort * 100}%`, display: 'inline-block' }} />
              </div>
            </div>
          </div>
        )}

        {/* Top 5 travleste — donut diagrammer (stacket vertikalt for smal sidebar) */}
        {(top5Islands.length > 0 || top5Bins.length > 0 || top5Maxi.length > 0) && (
          <div>
            <p className="text-[10px] text-zinc-500 font-mono uppercase mb-2">Top 5 mest travle</p>
            <div className="flex flex-col gap-2">
              {top5Islands.length > 0 && <DonutChart data={top5Islands} title="Ressourceøer" size={130} />}
              {top5Bins.length > 0 && <DonutChart data={top5Bins} title="Spande" size={130} />}
              {top5Maxi.length > 0 && <DonutChart data={top5Maxi} title="Maxi" size={130} />}
            </div>
          </div>
        )}

        {/* Sortering: Bedste 5 og Dårligste 5 — stacket vertikalt */}
        {best5.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5">
              <p className="text-[10px] text-zinc-500 font-mono uppercase mb-1.5 flex items-center gap-1"><TrendingUp className="h-3 w-3 text-green-400"/>Bedste 5</p>
              {best5.map((p, i) => (
                <div key={i} className="flex justify-between text-[10px] font-mono py-0.5">
                  <span className="text-zinc-400 truncate mr-1">{p.label}</span>
                  <span className="text-green-400 font-bold">{p.pct}%</span>
                </div>
              ))}
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5">
              <p className="text-[10px] text-zinc-500 font-mono uppercase mb-1.5 flex items-center gap-1"><TrendingDown className="h-3 w-3 text-red-400"/>Dårligste 5</p>
              {worst5.map((p, i) => (
                <div key={i} className="flex justify-between text-[10px] font-mono py-0.5">
                  <span className="text-zinc-400 truncate mr-1">{p.label}</span>
                  <span className="text-red-400 font-bold">{p.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DAGENS PROGRAM — fra Kalender. Hvert punkt vises indtil 30 min efter deadline. */}
        <div data-testid="live-monitor-todays-agenda">
          <p className="text-[10px] text-zinc-500 font-mono uppercase mb-2 flex items-center justify-between">
            <span>Dagens program</span>
            <span className="text-zinc-600 normal-case">
              {new Date(nowTs).toLocaleDateString('da-DK', { day: '2-digit', month: 'short' })}
            </span>
          </p>
          {todaysAgenda.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-center">
              <p className="text-[10px] text-zinc-600 italic">Ingen aktive punkter i dag</p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {todaysAgenda.map(item => (
                <div
                  key={item.id}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 flex items-start gap-2"
                  data-testid={`agenda-item-${item.id}`}
                >
                  <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ background: item.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-1.5">
                      <span className="text-[11px] font-bold text-white font-mono">
                        {item.time || 'Hele dagen'}
                      </span>
                      <span className="text-[8px] text-zinc-500 uppercase tracking-wider truncate">{item.category}</span>
                    </div>
                    <div className="text-[10.5px] text-zinc-300 leading-tight mt-0.5 break-words">
                      {item.title}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidst opdateret */}
        <div className="mt-auto text-center pt-2">
          <p className="text-[10px] text-zinc-600 font-mono">
            Opdateret: {lastUpdate ? lastUpdate.toLocaleTimeString('da-DK') : '-'}
          </p>
        </div>
      </div>
    </div>
  );
}
